// CookieConsent · GDPR / ePrivacy compliant banner that surfaces on
// first visit and stores the decision in `localStorage`.
//
// Behaviour
// ---------
// • Renders only when `localStorage.cookieConsent` is absent (never
//   re-prompts after the user has decided).
// • Three actions · Accept all · Reject non-essential · Customise.
// • Three categories · Essential (always-on, can't be toggled) ·
//   Analytics · Marketing.
// • Saves to `localStorage` as `{ ts, choice, prefs:{analytics,marketing} }`.
// • Fires a `cookie-consent-changed` window event so downstream
//   scripts (Tawk.to, GA, etc.) can opt-in or opt-out reactively.
// • Honours `Do Not Track: 1` browser header — auto-rejects non-
//   essential cookies and hides the banner.
// • Keyboard accessible · focus-trapped while open · escape closes.

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, Shield, BarChart3, Megaphone, X } from "lucide-react";

const STORAGE_KEY = "cookieConsent";

const DEFAULT_PREFS = { analytics: false, marketing: false };

function readConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function writeConsent(payload) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (_) { /* private mode · ignore */ }
  // Notify anything that cares (analytics scripts, Tawk loader, etc.)
  window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: payload }));
}

export default function CookieConsent() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [customise, setCustomise] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const existing = readConsent();
    if (existing) return; // already decided

    // Honour Do Not Track · auto-reject and skip the banner
    if (typeof navigator !== "undefined" && (navigator.doNotTrack === "1" || window.doNotTrack === "1")) {
      writeConsent({ ts: Date.now(), choice: "rejected", prefs: DEFAULT_PREFS, source: "dnt" });
      return;
    }

    // Delay banner so it doesn't fight the hero's entrance animation
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    writeConsent({ ts: Date.now(), choice: "accepted", prefs: { analytics: true, marketing: true } });
    setOpen(false);
  };
  const reject = () => {
    writeConsent({ ts: Date.now(), choice: "rejected", prefs: DEFAULT_PREFS });
    setOpen(false);
  };
  const saveCustom = () => {
    writeConsent({
      ts: Date.now(),
      choice: prefs.analytics || prefs.marketing ? "custom" : "rejected",
      prefs,
    });
    setOpen(false);
  };

  // Admin dashboard routes: never show the banner (it overlaps the
  // sticky Save button). The public-site prompt still appears normally.
  if (pathname.startsWith("/admin")) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="false"
          aria-label="Cookie preferences"
          data-testid="cookie-consent"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-2 bottom-[76px] sm:inset-x-auto sm:left-4 sm:bottom-4 sm:right-4 z-[120] max-w-4xl sm:mx-auto"
        >
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "linear-gradient(160deg, color-mix(in srgb, var(--surface-2) 96%, transparent) 0%, color-mix(in srgb, var(--surface) 96%, transparent) 100%)",
              border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              boxShadow: "var(--shadow-xl)",
            }}
          >
            {/* Top hairline */}
            <div className="h-px bg-gradient-to-r from-transparent via-brand/55 to-transparent" />

            {/* Header */}
            <div className="px-5 py-4 sm:px-6 sm:py-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0"
                     style={{
                       background: "color-mix(in srgb, var(--brand) 14%, transparent)",
                       border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
                     }}>
                  <Cookie size={16} className="text-brand" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="pill-mono inline-flex mb-2">{t("cookieConsent.pill", "Cookies")}</div>
                  <h2 className="font-display text-base sm:text-lg font-medium text-text-1 leading-tight">
                    {t("cookieConsent.title", "We use cookies to improve your trading experience")}
                  </h2>
                  <p className="text-text-3 text-[12.5px] sm:text-[13px] mt-1.5 font-light leading-relaxed">
                    {t("cookieConsent.body", "Essential cookies keep the platform secure and your preferences saved. Analytics and marketing cookies are optional and help us improve the site. Read the")}{" "}
                    <Link to="/legal/cookies" className="text-brand hover:underline">{t("footer.cookies", "Cookie Policy")}</Link>{" "}
                    {t("cookieConsent.orThe", "or the")}{" "}
                    <Link to="/legal/privacy" className="text-brand hover:underline">{t("footer.privacy", "Privacy Policy")}</Link>.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={reject}
                  className="ml-1 -mt-1 -mr-1 w-7 h-7 grid place-items-center rounded-md text-text-3 hover:text-text-1 hover:bg-[color:var(--surface-2)]"
                  data-testid="cookie-consent-close"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Customise panel */}
              <AnimatePresence initial={false}>
                {customise && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                    data-testid="cookie-consent-customise"
                  >
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <CategoryRow
                        Icon={Shield}
                        title={t("cookieConsent.essential.title", "Essential")}
                        desc={t("cookieConsent.essential.desc", "Authentication, security, language preference. Required.")}
                        checked
                        disabled
                      />
                      <CategoryRow
                        Icon={BarChart3}
                        title={t("cookieConsent.analytics.title", "Analytics")}
                        desc={t("cookieConsent.analytics.desc", "Aggregated usage data so we can improve the site.")}
                        checked={prefs.analytics}
                        onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                        testId="cookie-consent-toggle-analytics"
                      />
                      <CategoryRow
                        Icon={Megaphone}
                        title={t("cookieConsent.marketing.title", "Marketing")}
                        desc={t("cookieConsent.marketing.desc", "Helps us measure ad performance and re-engage past visitors.")}
                        checked={prefs.marketing}
                        onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                        testId="cookie-consent-toggle-marketing"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Action row */}
              <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
                {!customise ? (
                  <>
                    <button
                      type="button"
                      onClick={accept}
                      data-testid="cookie-consent-accept"
                      className="btn-solid-brand text-sm h-10 px-5 inline-flex items-center justify-center gap-1.5 w-full sm:w-auto"
                    >
                      {t("cookieConsent.acceptAll", "Accept all")}
                    </button>
                    <button
                      type="button"
                      onClick={reject}
                      data-testid="cookie-consent-reject"
                      className="text-sm h-10 px-5 rounded-xl border hover:bg-[color:var(--surface-2)] transition-colors w-full sm:w-auto"
                      style={{ borderColor: "var(--line-strong)", color: "var(--text-1)" }}
                    >
                      {t("cookieConsent.rejectAll", "Reject non-essential")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomise(true)}
                      data-testid="cookie-consent-customise-btn"
                      className="text-sm h-10 px-3 rounded-xl text-text-2 hover:text-brand transition-colors w-full sm:w-auto sm:ml-auto"
                    >
                      {t("cookieConsent.customise", "Customise")}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={saveCustom}
                      data-testid="cookie-consent-save"
                      className="btn-solid-brand text-sm h-10 px-5 inline-flex items-center justify-center w-full sm:w-auto"
                    >
                      {t("cookieConsent.save", "Save preferences")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setCustomise(false)}
                      className="text-sm h-10 px-3 rounded-xl text-text-2 hover:text-brand transition-colors w-full sm:w-auto"
                    >
                      {t("cookieConsent.back", "Back")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CategoryRow({ Icon, title, desc, checked, disabled, onChange, testId }) {
  return (
    <label
      className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer hover:bg-[color:var(--surface-2)] transition-colors"
      style={{
        borderColor: "color-mix(in srgb, var(--brand) 18%, transparent)",
        opacity: disabled ? 0.85 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <div className="w-8 h-8 rounded-md grid place-items-center shrink-0"
           style={{
             background: "color-mix(in srgb, var(--brand) 10%, transparent)",
             border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
           }}>
        <Icon size={14} className="text-brand" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-medium text-text-1">{title}</span>
          {disabled && <span className="pill-mono text-[12px] !py-0 !px-1.5">Always on</span>}
        </div>
        <p className="text-[12px] text-text-3 font-light leading-snug mt-0.5">{desc}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testId}
        className="mt-1 accent-brand"
      />
    </label>
  );
}
